import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Sparkles, Brain, LineChart, MessageSquare } from 'lucide-react';
import SEO from '../components/SEO';
import { BriefingIQMockup, EventIQMockup } from '../components/ProductMockups';
import HeroLeadForm from '../components/HeroLeadForm';
import { capabilities, aboutValues } from '../mock';

const HomePage = ({ onOpenDemo }) => {
  return (
    <>
      <SEO
        title="AI-Powered Enterprise Briefings &amp; Corporate Events"
        description="BriefingIQ and EventIQ — the AI-powered enterprise platform to plan, execute, and measure every executive briefing and corporate event. Built for global enterprise teams."
        path="/"
        keywords={[
          'AI briefing management',
          'AI event management',
          'executive briefing management',
          'corporate event management',
          'briefing center software',
          'event management platform',
          'Salesforce integration',
          'enterprise SaaS',
        ]}
      />

      {/* Hero */}
      <section className="bg-white hero-glow pt-16 pb-14 lg:pt-24 lg:pb-20 border-b border-[var(--line-2)]">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.15fr,0.85fr] gap-12 lg:gap-16 items-center mb-16">
          <div className="text-left">
            <div className="inline-flex items-center gap-2 border border-[var(--badge-border)] bg-[var(--badge-bg)] rounded-full pl-1 pr-3 py-1 mb-6 fade-up">
              <span className="text-[10px] font-semibold tracking-[0.15em] uppercase bg-[#4F46E5] text-white rounded-full px-2 py-0.5">AI</span>
              <span className="text-[11px] font-medium text-[#4F46E5]">Powered engagement platform</span>
            </div>
            <h1 className="headline-xl text-ink mb-6 fade-up-delay-1">
              The AI-powered platform<br />
              <span className="text-[#4F46E5]">for enterprise briefings &amp; events.</span>
            </h1>
            <p className="text-lg md:text-xl text-ink-2 max-w-xl leading-relaxed mb-9 fade-up-delay-2">
              BriefingIQ and EventIQ use AI to plan, run, and measure every customer engagement — from briefing intake and account intelligence, to live recaps, portfolio-level pattern detection, and pipeline attribution.
            </p>
            <div className="flex flex-wrap gap-4 items-center fade-up-delay-3">
              <Link to="/platform" className="btn-secondary">
                See the AI in action <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="btn-link">
                Talk to sales <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-xs text-ink-3 fade-up-delay-3">
              <span>500+ global enterprises</span>
              <span>Salesforce-native</span>
              <span>SOC 2 &amp; SSO ready</span>
            </div>
          </div>
          <div className="fade-up-delay-2 lg:justify-self-end w-full max-w-md">
            <HeroLeadForm />
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 fade-up-delay-3">
          <BriefingIQMockup />
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white border-t border-[var(--line-2)]">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-8">
            {[
              { value: '500+', label: 'Global enterprises' },
              { value: '2.4M', label: 'Engagements managed' },
              { value: '60%', label: 'Coordinator time saved with AI' },
              { value: '24/7', label: 'Enterprise support' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-4xl md:text-5xl font-semibold text-ink tracking-tight mb-2">{s.value}</div>
                <div className="text-sm text-ink-3">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI-powered showcase */}
      <section className="bg-[#0F172A] dark section relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="relative max-w-6xl mx-auto">
          <div className="max-w-3xl mb-14">
            <div className="inline-flex items-center gap-2 border border-[#4F46E5]/40 bg-[#4F46E5]/15 rounded-full pl-1 pr-3 py-1 mb-6">
              <span className="text-[10px] font-semibold tracking-[0.15em] uppercase bg-[#4F46E5] text-white rounded-full px-2 py-0.5">AI</span>
              <span className="text-[11px] text-[#A5B4FC]">Built into every workflow</span>
            </div>
            <h2 className="headline-lg text-white mb-6">
              AI that actually works,<br />
              <span className="text-white/60">in production, every day.</span>
            </h2>
            <p className="text-lg text-white/70 leading-relaxed">
              Not a chat sidebar. BriefingIQ &amp; EventIQ embed AI at every step of the engagement lifecycle — so your coordinators, executives, and account teams do their best work in less time.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {[
              {
                icon: Brain,
                stage: 'Before',
                title: 'Account intelligence in one click',
                body: 'Auto-generated pre-briefing packet pulled from Salesforce, LinkedIn, public filings, and prior engagements. Ready before the customer arrives.',
              },
              {
                icon: MessageSquare,
                stage: 'During',
                title: 'Live transcription &amp; structured recaps',
                body: 'Real-time speaker-identified transcription. A structured recap and a draft follow-up email — in your account owner&rsquo;s voice — the moment the room clears.',
              },
              {
                icon: LineChart,
                stage: 'After',
                title: 'Portfolio pattern detection',
                body: 'Clusters trending themes, questions, and competitors across every briefing and event. Your program becomes an intelligence signal, not a hospitality function.',
              },
              {
                icon: Sparkles,
                stage: 'Always',
                title: 'Next-best-action for every account',
                body: 'Ranked follow-up recommendations for every account team, updated as the pipeline moves. Nothing falls through the cracks.',
              },
            ].map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="bg-white/[0.04] border border-white/10 rounded-2xl p-7 lift">
                  <div className="flex items-center gap-2 mb-5">
                    <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                    <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-white/60">{f.stage}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2" dangerouslySetInnerHTML={{ __html: f.title }} />
                  <p className="text-sm text-white/65 leading-relaxed" dangerouslySetInnerHTML={{ __html: f.body }} />
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-4 items-center">
            <Link to="/platform" className="btn-invert">
              How the AI works <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/blog/ai-executive-engagement-workflows" className="text-sm text-white/70 hover:text-white inline-flex items-center gap-1 transition-colors">
              Read the AI workflows breakdown <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="bg-[var(--bg-alt)] section">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-4">Two products. One platform.</div>
            <h2 className="headline-lg text-ink mb-6">
              Purpose-built for how the modern enterprise engages.
            </h2>
            <p className="text-lg text-ink-2 leading-relaxed">
              BriefingIQ handles executive briefing management. EventIQ powers corporate event operations. Use one, or use both — with shared integrations, analytics, and a unified experience.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="lift bg-white rounded-3xl p-8 lg:p-10 border border-[var(--line-2)]">
              <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-3">Executive Briefing Management</div>
              <h3 className="headline-md text-ink mb-4">BriefingIQ</h3>
              <p className="text-ink-2 leading-relaxed mb-6">
                Plan, coordinate, and execute executive briefing programs with end-to-end workflow automation. Multi-location, CRM-native.
              </p>
              <div className="mb-6 rounded-2xl overflow-hidden border border-[var(--line-2)]">
                <BriefingIQMockup />
              </div>
              <Link to="/briefingiq" className="btn-primary">
                Explore BriefingIQ <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lift bg-white rounded-3xl p-8 lg:p-10 border border-[var(--line-2)]">
              <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-3">Corporate Event Management</div>
              <h3 className="headline-md text-ink mb-4">EventIQ</h3>
              <p className="text-ink-2 leading-relaxed mb-6">
                Manage conferences, summits, trade shows, and hybrid events with logistics automation and real-time analytics.
              </p>
              <div className="mb-6 rounded-2xl overflow-hidden border border-[var(--line-2)]">
                <EventIQMockup />
              </div>
              <Link to="/eventiq" className="btn-primary">
                Explore EventIQ <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="bg-white section">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-4">The Platform</div>
            <h2 className="headline-lg text-ink mb-6">
              Built for enterprise scale.<br />
              <span className="text-ink-3">Refined for everyday use.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--line-2)] rounded-3xl overflow-hidden border border-[var(--line-2)]">
            {capabilities.map((cap) => {
              const Icon = cap.icon;
              return (
                <div key={cap.title} className="bg-white p-8 lg:p-10">
                  <Icon className="w-6 h-6 text-ink mb-6" strokeWidth={1.5} />
                  <h3 className="text-lg font-semibold text-ink mb-3">{cap.title}</h3>
                  <p className="text-sm text-ink-2 leading-relaxed">{cap.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About preview */}
      <section className="bg-[var(--bg-alt)] section">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-4">About</div>
            <h2 className="headline-lg text-ink mb-6">
              Built by industry professionals, for industry professionals.
            </h2>
            <p className="text-lg text-ink-2 leading-relaxed mb-8">
              Our team brings decades of combined experience in briefing programs, event operations, enterprise SaaS, and AI — shaped by what practitioners actually need.
            </p>
            <Link to="/company" className="btn-secondary">
              About the company <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid gap-4">
            {aboutValues.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="bg-white rounded-2xl p-6 border border-[var(--line-2)] flex gap-4">
                  <Icon className="w-5 h-5 text-ink flex-shrink-0 mt-1" strokeWidth={1.5} />
                  <div>
                    <h4 className="text-base font-semibold text-ink mb-1">{v.title}</h4>
                    <p className="text-sm text-ink-2 leading-relaxed">{v.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0F172A] dark section relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-60" />
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-6">Get started today</div>
          <h2 className="headline-lg text-white mb-6">
            Ready to see BriefingIQ &amp; EventIQ in action?
          </h2>
          <p className="text-lg text-white/70 leading-relaxed mb-10 max-w-2xl mx-auto">
            See how the platform delivers world-class executive briefings, corporate events, and measurable customer engagement.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button onClick={onOpenDemo} className="btn-invert">
              Request a demo <ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/contact" className="btn-invert-outline">
              Contact sales
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
